import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { formatIDR, formatShortIDR } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight, Wallet, PiggyBank, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

const getFontSizeClass = (value, baseSize = "text-3xl") => {
  const len = value ? String(value).length : 0;
  if (baseSize === "text-3xl") {
    if (len > 16) return "text-base";
    if (len > 14) return "text-lg";
    if (len > 11) return "text-xl";
    if (len > 9) return "text-2xl";
    return "text-3xl";
  }
  // text-2xl base
  if (len > 16) return "text-sm";
  if (len > 14) return "text-base";
  if (len > 11) return "text-lg";
  if (len > 9) return "text-xl";
  return "text-2xl";
};

export default function Dashboard() {
  const { data: summary } = useSWR("/reports/summary", fetcher);
  const { data: txs } = useSWR("/transactions", fetcher);
  const { data: accounts } = useSWR("/accounts", fetcher);

  const monthly = summary?.monthly || [];
  const recent = (txs || []).slice(0, 8);

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <p className="label-caps">Overview</p>
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Dashboard</h1>
      </div>

      {/* Metric row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Net Worth"
          value={formatIDR(summary?.net_worth || 0)}
          icon={TrendingUp}
          testid="metric-net-worth"
        />
        <MetricCard
          label="Cash Balance"
          value={formatIDR(summary?.account_balance || 0)}
          icon={Wallet}
          testid="metric-cash-balance"
        />
        <MetricCard
          label="Total Income"
          value={formatIDR(summary?.total_income || 0)}
          icon={ArrowUpRight}
          gain
          testid="metric-total-income"
        />
        <MetricCard
          label="Total Expense"
          value={formatIDR(summary?.total_expense || 0)}
          icon={ArrowDownRight}
          loss
          testid="metric-total-expense"
        />
      </div>

      {/* Charts + feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card p-5">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="label-caps">Cash Flow • 12 months</p>
              <h3 className={`font-heading font-medium tracking-tight mt-1 whitespace-nowrap truncate ${getFontSizeClass(formatIDR(summary?.cash_flow || 0), "text-2xl")}`}>
                {formatIDR(summary?.cash_flow || 0)}
              </h3>
            </div>
          </div>
          <div className="h-72" data-testid="cashflow-chart">
            {monthly.length === 0 ? (
               <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Belum ada data. Tambahkan transaksi untuk melihat aliran uang.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={formatShortIDR} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      fontSize: 12,
                    }}
                    formatter={(v) => formatIDR(v)}
                  />
                  <Bar dataKey="income" fill="hsl(var(--gain))" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="expense" fill="hsl(var(--loss))" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="label-caps">Recent Activity</p>
            <Link to="/history" className="text-xs text-muted-foreground hover:text-foreground">View all</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Belum ada transaksi.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="recent-transactions">
              {recent.map((t) => (
                <li key={t.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.category}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.note || "—"} • {t.date}</p>
                  </div>
                  <span className={`text-sm font-mono ${t.type === "income" ? "text-gain" : "text-loss"}`}>
                    {t.type === "income" ? "+" : "-"}{formatIDR(t.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Accounts */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <p className="label-caps">Your Accounts</p>
          <Link to="/accounts" className="text-xs text-muted-foreground hover:text-foreground">Manage</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(accounts || []).map((a) => (
            <div key={a.id} className="border border-border bg-card p-4 relative overflow-hidden" data-testid={`account-tile-${a.id}`}>
              <div className="h-1 absolute top-0 left-0 right-0" style={{ background: a.color || "#22C55E" }} />
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{a.type}</p>
              <p className="font-heading text-lg mt-1">{a.name}</p>
              <p className={`metric-num mt-3 whitespace-nowrap truncate ${getFontSizeClass(formatIDR(a.balance), "text-2xl")}`}>{formatIDR(a.balance)}</p>
            </div>
          ))}
          {(!accounts || accounts.length === 0) && (
            <div className="col-span-full border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Tambahkan akun untuk mulai mencatat cash flow. <Link to="/accounts" className="underline">Kelola akun</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, gain, loss, testid }) {
  return (
    <div className="border border-border bg-card p-5 relative" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="label-caps">{label}</p>
        <Icon className={`h-4 w-4 ${gain ? "text-gain" : loss ? "text-loss" : "text-muted-foreground"}`} />
      </div>
      <p className={`metric-num tracking-tight mt-4 whitespace-nowrap truncate ${getFontSizeClass(value, "text-3xl")} ${gain ? "text-gain" : loss ? "text-loss" : ""}`}>{value}</p>
    </div>
  );
}
